import { z } from "zod/v4";

/**
 * Weekday codes for `recurring` invites — the canonical wire format stored in
 * `invite.recurrence` (comma-separated, e.g. "lun,mer,ven"). French short
 * codes; this order is canonical for serialization. Shared by web + mobile so
 * both serialize identically.
 *
 * Lives in its own module (not index.ts) so `pass-status.ts` can import these
 * primitives without creating a runtime import cycle with index.ts, which
 * re-exports pass-status.ts. Under Metro/Hermes (React Native) a value cycle
 * like that can evaluate to `undefined` at module-init time; this file has no
 * dependency on index.ts or pass-status.ts, so there is nothing to cycle.
 */
export const weekdays = [
  "lun",
  "mar",
  "mer",
  "jeu",
  "ven",
  "sam",
  "dim",
] as const;
export const weekdaySchema = z.enum(weekdays);
export type Weekday = (typeof weekdays)[number];

/** Code + French label for rendering weekday selectors. */
export const WEEKDAYS: { code: Weekday; label: string }[] = [
  { code: "lun", label: "Lun" },
  { code: "mar", label: "Mar" },
  { code: "mer", label: "Mer" },
  { code: "jeu", label: "Jeu" },
  { code: "ven", label: "Ven" },
  { code: "sam", label: "Sam" },
  { code: "dim", label: "Dim" },
];

/** Selected weekdays → canonical comma-separated `recurrence` string. */
export function weekdaysToRecurrence(days: Iterable<Weekday>): string {
  const set = new Set(days);
  return weekdays.filter((d) => set.has(d)).join(",");
}

/** `recurrence` string → ordered, de-duped, validated weekday codes. */
export function recurrenceToWeekdays(value: string): Weekday[] {
  const set = new Set(
    value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  );
  return weekdays.filter((d) => set.has(d));
}
